import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { ErrorTypography } from "components/ErrorTypography";
import { ClientAppEvent, EVOTRUX_BORDER_GREY } from "constants/index";
import { useDisclosure, useWriterComponentState } from "hooks";
import { formatCentsWithCurrency } from "utils";

import type { EditableLineItem } from "./TfcEditableTable";

interface TfcResubmitButtonProps {
  tfcId: number;
  lineItems: EditableLineItem[];
  note: string;
  bookedPriceCents: number;
  currency: string;
  disabled: boolean;
  onSuccess: () => void;
}

export function TfcResubmitButton(props: TfcResubmitButtonProps) {
  const {
    tfcId,
    lineItems,
    note,
    bookedPriceCents,
    currency,
    disabled,
    onSuccess,
  } = props;
  const { isOpen, onOpen, onClose } = useDisclosure(false);
  const posthog = usePostHog();

  const totalCents =
    bookedPriceCents +
    lineItems.reduce((sum, item) => sum + item.line_total_cents, 0);
  const formattedTotal = formatCentsWithCurrency(totalCents, currency);

  const { loading, write, error } = useWriterComponentState({
    errorMessage: "Failed to resubmit freight cost. Please try again.",
    writeConfig: {
      method: "post",
      url: `/tfc/${tfcId}/resubmit`,
      body: {
        line_items: lineItems.map(({ description, line_total_cents }) => ({
          description,
          line_total_cents,
        })),
        notes: note || undefined,
      },
    },
  });

  const handleConfirm = () => {
    onClose();
    write(() => {
      posthog?.capture(ClientAppEvent.TFC_RESUBMITTED);
      onSuccess();
    });
  };

  return (
    <>
      <Button
        variant="contained"
        disabled={disabled || loading}
        onClick={onOpen}
        sx={{
          px: 3,
          "&.Mui-disabled": {
            backgroundColor: EVOTRUX_BORDER_GREY,
            color: "#9CA3AF",
          },
        }}
      >
        SUBMIT TO SHIPPER
      </Button>

      <ErrorTypography error={error} />

      <Dialog
        open={isOpen}
        onClose={onClose}
        PaperProps={{ sx: { borderRadius: "8px", p: 1, maxWidth: "440px" } }}
      >
        <DialogTitle sx={{ fontSize: "17px", fontWeight: 500, pb: 1 }}>
          Resubmit Freight Cost
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            component="div"
            sx={{ fontSize: "14px", color: "#4B5563" }}
          >
            You&apos;re resubmitting a total freight cost of{" "}
            <strong>{formattedTotal}</strong> to the shipper for review.
            Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={onClose} sx={{ px: 3 }}>
            CANCEL
          </Button>
          <Button variant="contained" onClick={handleConfirm} sx={{ px: 3 }}>
            CONFIRM
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
